/**
 * Math Type Validators — clock, money, columnArithmetic, ratioTable,
 * numberSequence, gridShade, stepSolver, comparisonJudge
 *
 * Split from typeStage.ts (2786 LOC god-file) — P2-S2
 */

import type { ValidationIssue } from './shared'
import { STAGE, blocker, coach } from './shared'
import { SEVERITY } from '../../severityScale'

// ─── Clock Validation ─────────────────────────────────────────────────────────

export function validateClock(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const validModes = [
    'read_analog_fill',
    'read_digital_fill',
    'analog_to_digital_mc',
    'digital_to_analog_mc',
    'set_analog',
  ]
  if (data.mode && !validModes.includes(data.mode as string)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_CLOCK_INVALID_MODE',
        `Invalid clock mode: "${data.mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: String(data.mode),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  const target = data.target as Record<string, unknown> | undefined
  if (target && typeof target === 'object') {
    const hours = target.hours as number
    const minutes = target.minutes as number

    if (
      typeof hours !== 'number' ||
      hours < 0 ||
      hours > 23 ||
      !Number.isInteger(hours)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_INVALID_HOURS',
          `Clock hours must be integer 0-23, got: ${hours}`,
          {
            field: 'data.target.hours',
            expected: '0-23',
            actual: String(hours),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (
      typeof minutes !== 'number' ||
      minutes < 0 ||
      minutes > 59 ||
      !Number.isInteger(minutes)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_INVALID_MINUTES',
          `Clock minutes must be integer 0-59, got: ${minutes}`,
          {
            field: 'data.target.minutes',
            expected: '0-59',
            actual: String(minutes),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (data.mode && String(data.mode).endsWith('_mc')) {
    if (
      !data.options ||
      !Array.isArray(data.options) ||
      (data.options as unknown[]).length < 2
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_MC_MISSING_OPTIONS',
          'Clock MC mode requires options array with ≥2 entries',
          {
            field: 'data.options',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Money Validation ─────────────────────────────────────────────────────────

export function validateMoney(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const validModes = ['make_amount', 'give_change']
  if (data.mode && !validModes.includes(data.mode as string)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MONEY_INVALID_MODE',
        `Invalid money mode: "${data.mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: String(data.mode),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  const targetCents = data.targetCents as number
  if (typeof targetCents === 'number') {
    if (targetCents <= 0 || !Number.isInteger(targetCents)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_INVALID_TARGET',
          `targetCents must be positive integer, got: ${targetCents}`,
          {
            field: 'data.targetCents',
            expected: 'positive integer',
            actual: String(targetCents),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  const denoms = data.allowedDenoms
  if (denoms && Array.isArray(denoms)) {
    for (let i = 0; i < (denoms as unknown[]).length; i++) {
      const d = (denoms as Record<string, unknown>[])[i]
      if (!d || typeof d !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM',
            `allowedDenoms[${i}] must be an object`,
            {
              field: `data.allowedDenoms[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
        continue
      }
      if (typeof d.valueCents !== 'number' || (d.valueCents as number) <= 0) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM_VALUE',
            `allowedDenoms[${i}].valueCents must be positive number`,
            {
              field: `data.allowedDenoms[${i}].valueCents`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      if (d.type !== 'coin' && d.type !== 'bill') {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM_TYPE',
            `allowedDenoms[${i}].type should be "coin" or "bill"`,
            {
              field: `data.allowedDenoms[${i}].type`,
              expected: 'coin | bill',
              actual: String(d.type),
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
            },
          ),
        )
      }
    }
  } else if (denoms !== undefined) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MONEY_DENOMS_NOT_ARRAY',
        'allowedDenoms must be an array',
        {
          field: 'data.allowedDenoms',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (data.mode === 'give_change') {
    if (
      typeof data.priceCents !== 'number' ||
      (data.priceCents as number) <= 0
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_MISSING_PRICE',
          'give_change mode requires positive priceCents',
          {
            field: 'data.priceCents',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    if (typeof data.paidCents !== 'number' || (data.paidCents as number) <= 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_MISSING_PAID',
          'give_change mode requires positive paidCents',
          {
            field: 'data.paidCents',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    if (
      typeof data.paidCents === 'number' &&
      typeof data.priceCents === 'number' &&
      (data.paidCents as number) < (data.priceCents as number)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_PAID_LESS_THAN_PRICE',
          'paidCents must be ≥ priceCents for give_change',
          {
            field: 'data.paidCents',
            expected: `≥ ${data.priceCents}`,
            actual: String(data.paidCents),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Column Arithmetic Validation ─────────────────────────────────────────────

export function validateColumnArithmetic(
  data: Record<string, unknown>,
  type: string,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const op1 = data.operand1 as number | undefined
  const op2 = data.operand2 as number | undefined

  if (typeof op1 === 'number' && typeof op2 === 'number') {
    if (op1 <= 0 || op2 <= 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_COLUMN_NEGATIVE_OPERAND',
          'Column arithmetic operands must be positive',
          {
            field: 'data.operand1',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    const operation = data.operation as string | undefined
    if (
      (operation === '-' ||
        operation === '−' ||
        type === 'cijferend_aftrekken') &&
      op1 < op2
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_COLUMN_SUB_NEGATIVE_RESULT',
          'For subtraction, operand1 must be ≥ operand2',
          {
            field: 'data.operand1',
            expected: `≥ ${op2}`,
            actual: String(op1),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (!Number.isInteger(op1) || !Number.isInteger(op2)) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_COLUMN_NON_INTEGER',
          'Column arithmetic typically uses integers',
          {
            field: 'data.operand1',
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.LOW,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Ratio Table Validation ───────────────────────────────────────────────────

export function validateRatioTable(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[] | undefined
  const hiddenCells = data.hiddenCells as unknown[] | undefined

  if (headers && Array.isArray(headers)) {
    if (headers.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_RATIO_TABLE_HEADERS',
          'Ratio table should have at least 2 headers',
          {
            field: 'data.headers',
            expected: '≥2',
            actual: String(headers.length),
            questionId: qId,
            familyId: fId,
            // Ruis-audit v1.4 §25 T1.2: was SEVERITY.MEDIUM (45) — onder
            // REVIEW-drempel gebracht om severity-stacking te vermijden.
            severity: SEVERITY.NEAR_LOW,
          },
        ),
      )
    }
  }

  if (rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      const expectedCols =
        headers && Array.isArray(headers) ? headers.length : 2
      if (!Array.isArray(row) || (row as unknown[]).length !== expectedCols) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_RATIO_TABLE_ROW_FORMAT',
            `rows[${i}] should have ${expectedCols} columns (matching headers)`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_REVIEW,
            },
          ),
        )
      }
    }
  }

  if (!hiddenCells || !Array.isArray(hiddenCells)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_RATIO_TABLE_NO_HIDDEN_CELLS',
        'Ratio table requires data.hiddenCells array',
        {
          field: 'data.hiddenCells',
          questionId: qId,
          familyId: fId,
          suggestion: 'Add data.hiddenCells = [{row: 0, col: 1}, ...]',
        },
      ),
    )
  } else {
    if (hiddenCells.length === 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_RATIO_TABLE_EMPTY_HIDDEN_CELLS',
          'Ratio table must have at least one hidden cell',
          {
            field: 'data.hiddenCells',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < hiddenCells.length; i++) {
      const cell = hiddenCells[i] as Record<string, unknown> | undefined
      if (!cell || typeof cell !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_RATIO_TABLE_INVALID_HIDDEN_CELL',
            `hiddenCells[${i}] must be an object with row and col`,
            {
              field: `data.hiddenCells[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (typeof cell.row !== 'number' || typeof cell.col !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_RATIO_TABLE_INVALID_CELL_COORDS',
              `hiddenCells[${i}] must have numeric row and col`,
              {
                field: `data.hiddenCells[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }

        if (rows && Array.isArray(rows)) {
          const rowIdx = cell.row as number
          const colIdx = cell.col as number
          if (rowIdx < 0 || rowIdx >= rows.length) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_RATIO_TABLE_ROW_OUT_OF_BOUNDS',
                `hiddenCells[${i}].row (${rowIdx}) out of range [0, ${rows.length - 1}]`,
                {
                  field: `data.hiddenCells[${i}].row`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
          const maxCol =
            headers && Array.isArray(headers) ? headers.length - 1 : 1
          if (colIdx < 0 || colIdx > maxCol) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_RATIO_TABLE_COL_OUT_OF_BOUNDS',
                `hiddenCells[${i}].col (${colIdx}) must be 0-${maxCol}`,
                {
                  field: `data.hiddenCells[${i}].col`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
        }
      }
    }
  }

  return issues
}

// ─── Number Sequence Validation ───────────────────────────────────────────────

export function validateNumberSequence(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const sequence = data.sequence as unknown[] | undefined
  const missingIndex = data.missingIndex as number | undefined

  if (sequence && Array.isArray(sequence)) {
    if (sequence.length < 3) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_NUMBER_SEQ_TOO_SHORT',
          'Number sequence should have at least 3 elements',
          {
            field: 'data.sequence',
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MEDIUM,
          },
        ),
      )
    }

    if (typeof missingIndex === 'number') {
      if (missingIndex < 0 || missingIndex >= sequence.length) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_NUMBER_SEQ_INVALID_INDEX',
            `missingIndex ${missingIndex} out of range [0, ${sequence.length - 1}]`,
            {
              field: 'data.missingIndex',
              expected: `0-${sequence.length - 1}`,
              actual: String(missingIndex),
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  // Accept either `correctValue` (singular) OR `correctValues` (non-empty array).
  // Runtime validator (src/content/questions/validators/mathValidators.ts) supports
  // both forms; governance must mirror that to avoid false-positive blocks on
  // seeds written per AI_SEED_GENERATION_RULES.md (which documents the plural form).
  const hasSingular =
    data.correctValue !== undefined && data.correctValue !== null
  const correctValuesArr = data.correctValues
  const hasPlural =
    Array.isArray(correctValuesArr) && correctValuesArr.length > 0

  if (!hasSingular && !hasPlural) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_NUMBER_SEQ_NO_ANSWER',
        'Number sequence requires correctValue or non-empty correctValues array',
        {
          field: 'data.correctValue',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Grid Shade Validation ────────────────────────────────────────────────────

export function validateGridShade(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const gridRows = data.gridRows as number | undefined
  const gridCols = data.gridCols as number | undefined
  const targetCount = data.targetCount as number | undefined
  const mode = data.mode as string | undefined

  if (
    typeof gridRows !== 'number' ||
    gridRows < 1 ||
    !Number.isInteger(gridRows)
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_GRID_SHADE_INVALID_DIMS',
        'grid_shade requires data.gridRows as positive integer',
        {
          field: 'data.gridRows',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof gridCols !== 'number' ||
    gridCols < 1 ||
    !Number.isInteger(gridCols)
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_GRID_SHADE_INVALID_DIMS',
        'grid_shade requires data.gridCols as positive integer',
        {
          field: 'data.gridCols',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof targetCount === 'number' &&
    typeof gridRows === 'number' &&
    typeof gridCols === 'number'
  ) {
    if (targetCount < 1 || targetCount > gridRows * gridCols) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_GRID_SHADE_INVALID_TARGET',
          `targetCount (${targetCount}) must be between 1 and ${gridRows * gridCols}`,
          {
            field: 'data.targetCount',
            expected: `1-${gridRows * gridCols}`,
            actual: String(targetCount),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  const validModes = ['shade_count', 'shade_fraction', 'shade_to_match']
  if (mode && !validModes.includes(mode)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_GRID_SHADE_INVALID_MODE',
        `Invalid grid_shade mode: "${mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: mode,
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Step Solver Validation ───────────────────────────────────────────────────

export function validateStepSolver(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const steps = data.steps as unknown[] | undefined

  if (!steps || !Array.isArray(steps)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_STEP_SOLVER_NO_STEPS',
        'step_solver requires data.steps array',
        {
          field: 'data.steps',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (steps.length < 1) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_STEP_SOLVER_NO_STEPS',
        'step_solver must have at least 1 step',
        {
          field: 'data.steps',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i] as Record<string, unknown> | undefined
    if (!step || typeof step !== 'object') {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_STEP_SOLVER_INVALID_STEP',
          `steps[${i}] must be an object with { prompt, correctAnswer }`,
          {
            field: `data.steps[${i}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    } else {
      if (
        typeof step.prompt !== 'string' ||
        (step.prompt as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_STEP_SOLVER_INVALID_STEP',
            `steps[${i}].prompt must be a non-empty string`,
            {
              field: `data.steps[${i}].prompt`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      // Accept either `correctAnswer` (single value) OR `acceptedAnswers`
      // (array of accepted answers — the shape the practice-side runtime
      // validator in src/content/questions/validators/mathValidators.ts
      // actually uses). Parallels the validateNumberSequence fix:
      // governance must accept the same canonical shape as runtime.
      const hasCorrectAnswer =
        step.correctAnswer !== undefined && step.correctAnswer !== null
      const accepted = step.acceptedAnswers as unknown
      const hasAcceptedAnswers = Array.isArray(accepted) && accepted.length > 0
      if (!hasCorrectAnswer && !hasAcceptedAnswers) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_STEP_SOLVER_INVALID_STEP',
            `steps[${i}] requires either correctAnswer (single value) or acceptedAnswers (non-empty array)`,
            {
              field: `data.steps[${i}].correctAnswer`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Comparison Judge Validation ──────────────────────────────────────────────

export function validateComparisonJudge(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const items = data.items as unknown[] | undefined
  const criterion = data.criterion as string | undefined
  const correctIndex = data.correctIndex as number | undefined

  if (!items || !Array.isArray(items)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_COMPARISON_TOO_FEW_ITEMS',
        'comparison_judge requires data.items array',
        {
          field: 'data.items',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
    return issues
  }

  if (items.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_COMPARISON_TOO_FEW_ITEMS',
        'comparison_judge should have at least 2 items',
        {
          field: 'data.items',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.MEDIUM,
        },
      ),
    )
  }

  if (typeof criterion !== 'string' || criterion.trim() === '') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_COMPARISON_NO_CRITERION',
        'comparison_judge requires non-empty data.criterion',
        {
          field: 'data.criterion',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof correctIndex !== 'number' ||
    correctIndex < 0 ||
    correctIndex >= items.length ||
    !Number.isInteger(correctIndex)
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_COMPARISON_INVALID_INDEX',
        `correctIndex must be a valid index into items [0, ${items.length - 1}]`,
        {
          field: 'data.correctIndex',
          expected: `0-${items.length - 1}`,
          actual: String(correctIndex),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Multiplication Grid Validation ───────────────────────────────────────────

export function validateMultiplicationGrid(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const mode = data.mode as string | undefined
  const validModes = ['single_table', 'cross_table']

  if (mode && !validModes.includes(mode)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MULT_GRID_INVALID_MODE',
        `Invalid multiplication_grid mode: "${mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: mode,
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (mode === 'single_table') {
    const baseNumber = data.baseNumber as number | undefined
    const range = data.range as unknown[] | undefined
    const hiddenCells = data.hiddenCells as unknown[] | undefined

    if (
      typeof baseNumber !== 'number' ||
      baseNumber < 1 ||
      !Number.isInteger(baseNumber)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MULT_GRID_INVALID_BASE',
          'single_table mode requires data.baseNumber as positive integer',
          {
            field: 'data.baseNumber',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (!range || !Array.isArray(range) || range.length < 2) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MULT_GRID_INVALID_RANGE',
          'single_table mode requires data.range array with ≥2 entries',
          {
            field: 'data.range',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (hiddenCells && Array.isArray(hiddenCells)) {
      if (hiddenCells.length === 0) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_MULT_GRID_NO_HIDDEN',
            'multiplication_grid should have at least 1 hidden cell',
            {
              field: 'data.hiddenCells',
              questionId: qId,
              familyId: fId,
              severity: SEVERITY.NEAR_MEDIUM,
            },
          ),
        )
      }
      if (range && Array.isArray(range)) {
        for (let i = 0; i < hiddenCells.length; i++) {
          const idx = hiddenCells[i] as number
          if (typeof idx !== 'number' || idx < 0 || idx >= range.length) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_MULT_GRID_HIDDEN_OOB',
                `hiddenCells[${i}] (${idx}) out of range [0, ${range.length - 1}]`,
                {
                  field: `data.hiddenCells[${i}]`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
        }
      }
    }
  }

  if (mode === 'cross_table') {
    const rows = data.rows as unknown[] | undefined
    const cols = data.cols as unknown[] | undefined
    const hiddenCells = data.hiddenCells as unknown[] | undefined

    if (!rows || !Array.isArray(rows) || rows.length < 2) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MULT_GRID_INVALID_ROWS',
          'cross_table mode requires data.rows array with ≥2 entries',
          {
            field: 'data.rows',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (!cols || !Array.isArray(cols) || cols.length < 2) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MULT_GRID_INVALID_COLS',
          'cross_table mode requires data.cols array with ≥2 entries',
          {
            field: 'data.cols',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (hiddenCells && Array.isArray(hiddenCells)) {
      for (let i = 0; i < hiddenCells.length; i++) {
        const cell = hiddenCells[i] as Record<string, unknown> | undefined
        if (!cell || typeof cell !== 'object') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MULT_GRID_INVALID_HIDDEN',
              `hiddenCells[${i}] must be an object with { row, col }`,
              {
                field: `data.hiddenCells[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        } else {
          if (typeof cell.row !== 'number' || typeof cell.col !== 'number') {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_MULT_GRID_INVALID_HIDDEN',
                `hiddenCells[${i}] must have numeric row and col`,
                {
                  field: `data.hiddenCells[${i}]`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
          if (rows && Array.isArray(rows) && typeof cell.row === 'number') {
            if (
              (cell.row as number) < 0 ||
              (cell.row as number) >= rows.length
            ) {
              issues.push(
                blocker(
                  'type',
                  STAGE,
                  'TYPE_MULT_GRID_HIDDEN_OOB',
                  `hiddenCells[${i}].row (${cell.row}) out of range [0, ${rows.length - 1}]`,
                  {
                    field: `data.hiddenCells[${i}].row`,
                    questionId: qId,
                    familyId: fId,
                  },
                ),
              )
            }
          }
          if (cols && Array.isArray(cols) && typeof cell.col === 'number') {
            if (
              (cell.col as number) < 0 ||
              (cell.col as number) >= cols.length
            ) {
              issues.push(
                blocker(
                  'type',
                  STAGE,
                  'TYPE_MULT_GRID_HIDDEN_OOB',
                  `hiddenCells[${i}].col (${cell.col}) out of range [0, ${cols.length - 1}]`,
                  {
                    field: `data.hiddenCells[${i}].col`,
                    questionId: qId,
                    familyId: fId,
                  },
                ),
              )
            }
          }
        }
      }
    }
  }

  return issues
}

// ─── Estimation Slider Validation ─────────────────────────────────────────────

export function validateEstimationSlider(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const min = data.min as number | undefined
  const max = data.max as number | undefined
  const correctValue = data.correctValue as number | undefined
  const tolerancePercent = data.tolerancePercent as number | undefined

  if (typeof min !== 'number') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_EST_SLIDER_NO_MIN',
        'estimation_slider requires numeric data.min',
        {
          field: 'data.min',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (typeof max !== 'number') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_EST_SLIDER_NO_MAX',
        'estimation_slider requires numeric data.max',
        {
          field: 'data.max',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (typeof min === 'number' && typeof max === 'number' && min >= max) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_EST_SLIDER_MIN_GTE_MAX',
        `data.min (${min}) must be less than data.max (${max})`,
        {
          field: 'data.min',
          expected: `< ${max}`,
          actual: String(min),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (typeof correctValue !== 'number') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_EST_SLIDER_NO_VALUE',
        'estimation_slider requires numeric data.correctValue',
        {
          field: 'data.correctValue',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  } else if (typeof min === 'number' && typeof max === 'number') {
    if (correctValue < min || correctValue > max) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_EST_SLIDER_VALUE_OOB',
          `correctValue (${correctValue}) must be between min (${min}) and max (${max})`,
          {
            field: 'data.correctValue',
            expected: `${min}-${max}`,
            actual: String(correctValue),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (typeof tolerancePercent === 'number') {
    if (tolerancePercent < 1 || tolerancePercent > 50) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_EST_SLIDER_TOLERANCE',
          `tolerancePercent (${tolerancePercent}) should be between 1 and 50`,
          {
            field: 'data.tolerancePercent',
            expected: '1-50',
            actual: String(tolerancePercent),
            questionId: qId,
            familyId: fId,
            severity: SEVERITY.NEAR_MEDIUM,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Thermometer Slider Validation ────────────────────────────────────────────

export function validateThermometerSlider(
  data: Record<string, unknown>,
  correctAnswer: unknown,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const minTemp = data.minTemp as number | undefined
  const maxTemp = data.maxTemp as number | undefined
  const step = data.step as number | undefined

  if (typeof minTemp !== 'number') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_THERM_NO_MIN',
        'thermometer_slider requires numeric data.minTemp',
        {
          field: 'data.minTemp',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (typeof maxTemp !== 'number') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_THERM_NO_MAX',
        'thermometer_slider requires numeric data.maxTemp',
        {
          field: 'data.maxTemp',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof minTemp === 'number' &&
    typeof maxTemp === 'number' &&
    minTemp >= maxTemp
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_THERM_MIN_GTE_MAX',
        `data.minTemp (${minTemp}) must be less than data.maxTemp (${maxTemp})`,
        {
          field: 'data.minTemp',
          expected: `< ${maxTemp}`,
          actual: String(minTemp),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (typeof step !== 'number' || step <= 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_THERM_INVALID_STEP',
        'thermometer_slider requires positive data.step',
        {
          field: 'data.step',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  if (
    typeof correctAnswer === 'number' &&
    typeof minTemp === 'number' &&
    typeof maxTemp === 'number'
  ) {
    if (correctAnswer < minTemp || correctAnswer > maxTemp) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_THERM_ANSWER_OOB',
          `correctAnswer (${correctAnswer}) must be between minTemp (${minTemp}) and maxTemp (${maxTemp})`,
          {
            field: 'correctAnswer',
            expected: `${minTemp}-${maxTemp}`,
            actual: String(correctAnswer),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Stub Validators (Ruis-audit v1.6 T2.2) ───────────────────────────────────
//
// Minimal structural validators for specialized types that previously fell
// through the typeStage switch to `default: return []`. These check that the
// expected `data.{type}` namespace exists with the required top-level fields
// — same checks the renderers use to show their amber "Configuratie
// ontbreekt" fallback. Catches authoring mistakes at validation-time instead
// of render-time.
//
// Not deep validators — semantic constraints (e.g. targetValue must be a
// perfect square) are deliberately out of scope. Promote a stub to a full
// validator when concrete failure modes emerge from production seeds.

export function validateSquareRootBuilder(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  // resolveTypeData() in typeStage.ts maps `data.squareRootBuilder` → `data`
  // when nested config exists, otherwise returns the raw data object.
  const targetValue = data.targetValue
  if (
    typeof targetValue !== 'number' ||
    !Number.isFinite(targetValue) ||
    targetValue < 1
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_SQUARE_ROOT_BUILDER_INVALID_CONFIG',
        'square_root_builder requires data.squareRootBuilder.targetValue as a positive number',
        {
          field: 'data.squareRootBuilder.targetValue',
          actual: String(targetValue),
          questionId: qId,
          familyId: fId,
          suggestion:
            'Set data.squareRootBuilder.targetValue to the area the student must build (e.g. 16 for a 4×4 square).',
        },
      ),
    )
  }
  const maxSize = data.maxSize
  if (
    maxSize !== undefined &&
    (typeof maxSize !== 'number' || maxSize < 1 || maxSize > 20)
  ) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_SQUARE_ROOT_BUILDER_INVALID_CONFIG',
        `data.squareRootBuilder.maxSize should be 1–20 when set (got ${maxSize})`,
        {
          field: 'data.squareRootBuilder.maxSize',
          actual: String(maxSize),
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.LOW,
        },
      ),
    )
  }
  return issues
}

export function validatePlaceValueBuild(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  // Renderer accepts namespaced (data.placeValueBuild) OR legacy (data root).
  // resolveTypeData() prefers nested if present, so `data` here is whichever
  // is in use. We check the same fields the renderer reads.
  const target = data.target
  if (typeof target !== 'number' || !Number.isFinite(target) || target < 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_PLACE_VALUE_BUILD_INVALID_CONFIG',
        'place_value_build requires a numeric target (the number the student must compose)',
        {
          field: 'data.placeValueBuild.target',
          actual: String(target),
          questionId: qId,
          familyId: fId,
          suggestion:
            'Set data.placeValueBuild.target to the target number (e.g. 1234 to build with MAB-blocks).',
        },
      ),
    )
  }
  const initial = data.initial
  if (initial !== undefined && initial !== null) {
    if (typeof initial !== 'object' || Array.isArray(initial)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_PLACE_VALUE_BUILD_INVALID_CONFIG',
          'data.placeValueBuild.initial must be an object with thousands/hundreds/tens/ones counts',
          {
            field: 'data.placeValueBuild.initial',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }
  return issues
}

export function validateBarModelPart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const total = data.total
  const segments = data.segments
  const highlighted = data.highlighted

  if (typeof total !== 'number' || !Number.isFinite(total) || total <= 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_BAR_MODEL_PART_INVALID_CONFIG',
        'bar_model_part requires data.barModelPart.total as a positive number (the whole the bar represents)',
        {
          field: 'data.barModelPart.total',
          actual: String(total),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }
  if (
    typeof segments !== 'number' ||
    !Number.isInteger(segments) ||
    segments < 2
  ) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_BAR_MODEL_PART_INVALID_CONFIG',
        'bar_model_part requires data.barModelPart.segments as an integer ≥ 2 (how many equal parts the bar is split into)',
        {
          field: 'data.barModelPart.segments',
          actual: String(segments),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }
  if (!Array.isArray(highlighted) || highlighted.length === 0) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_BAR_MODEL_PART_INVALID_CONFIG',
        'bar_model_part requires data.barModelPart.highlighted as a non-empty array of segment indices',
        {
          field: 'data.barModelPart.highlighted',
          questionId: qId,
          familyId: fId,
          suggestion:
            'Provide highlighted as an array of 0-based segment indices, e.g. [0, 1] to highlight the first two segments.',
        },
      ),
    )
  } else if (typeof segments === 'number') {
    // Validate indices are within range
    const oob = (highlighted as unknown[]).filter(
      (h) => typeof h !== 'number' || h < 0 || h >= segments,
    )
    if (oob.length > 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_BAR_MODEL_PART_INVALID_CONFIG',
          `bar_model_part: ${oob.length} highlighted index/indices out of range [0, ${segments - 1}]`,
          {
            field: 'data.barModelPart.highlighted',
            expected: `indices in [0, ${segments - 1}]`,
            actual: JSON.stringify(highlighted),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }
  return issues
}

export function validateStrategySolver(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const strategies = data.strategies
  const expression = data.expression
  const acceptedAnswers = data.acceptedAnswers

  if (!Array.isArray(strategies) || strategies.length < 2) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_STRATEGY_SOLVER_INVALID_CONFIG',
        'strategy_solver requires data.strategySolver.strategies as an array of ≥2 strategy options',
        {
          field: 'data.strategySolver.strategies',
          actual: Array.isArray(strategies)
            ? `array(${strategies.length})`
            : String(strategies),
          questionId: qId,
          familyId: fId,
          suggestion:
            'Provide 3–5 strategy objects with { id, name, icon?, hint? }. The student picks one before answering.',
        },
      ),
    )
  } else {
    // Verify each strategy has an id (used as React key + payload value)
    const malformed = (strategies as unknown[]).filter(
      (s) =>
        typeof s !== 'object' ||
        s === null ||
        typeof (s as Record<string, unknown>).id !== 'string' ||
        ((s as Record<string, unknown>).id as string).trim() === '',
    )
    if (malformed.length > 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_STRATEGY_SOLVER_INVALID_CONFIG',
          `strategy_solver: ${malformed.length} strategy/strategies missing a non-empty string "id"`,
          {
            field: 'data.strategySolver.strategies[].id',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (expression !== undefined && typeof expression !== 'string') {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_STRATEGY_SOLVER_INVALID_CONFIG',
        'data.strategySolver.expression should be a string when set',
        {
          field: 'data.strategySolver.expression',
          actual: typeof expression,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.LOW,
        },
      ),
    )
  }

  // acceptedAnswers is optional — falls back to question.correctAnswer in
  // the renderer. Only warn if present but empty.
  if (Array.isArray(acceptedAnswers) && acceptedAnswers.length === 0) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_STRATEGY_SOLVER_INVALID_CONFIG',
        'data.strategySolver.acceptedAnswers is an empty array — remove it or populate it with valid answer variants',
        {
          field: 'data.strategySolver.acceptedAnswers',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.LOW,
        },
      ),
    )
  }

  return issues
}
